





















import { Vue, Component, Watch, Prop } from 'vue-property-decorator';

import { Surface } from '@/components/atoms';
import CloseModalIcon from '@/components/icons/CloseModal.vue';

import eventBus from '@/utils/eventBus';
import segmentIo from '@/utils/segment.io';

import Error from './Steps/Error.vue';
import Welcome from './Steps/Welcome.vue';
import ImportInProgress from './Steps/ImportInProgress.vue';
import ImportSuccess from './Steps/ImportSuccess.vue';
import OfferCreation from './Steps/OfferCreation.vue';
import Success from './Steps/Success.vue';
import { StartGuideTask } from '@/models/start-guide';

export enum ECustomerImportSteps {
  WELCOME = 1,
  IMPORT_IN_PROGRESS = 2,
  IMPORT_SUCCESS = 3,
  OFFER_CREATION = 4,
  SUCCESS = 5,
  ERROR = 6
}

export type CustomerImportFormData = {
  numberOfCustomers: number;
};

type CustomerImportStep = {
  componentName: string;
  analytics?: {
    pageViewLabel: string;
  };
};

type CustomerImportEvent = {
  type: string;
  params?: any;
};

@Component({
  components: {
    CloseModalIcon,
    Surface,
    Welcome,
    ImportInProgress,
    ImportSuccess,
    OfferCreation,
    Success,
    Error
  }
})
export default class CustomerImportModal extends Vue {
  @Prop() defaultStep?: ECustomerImportSteps;
  @Prop({ default: false }) disableStatusPolling?: boolean;
  @Prop() customerImportFormData!: CustomerImportFormData;
  @Prop() task?: StartGuideTask;
  @Prop() onConfirmUpdateStartGuide!: () => void;

  activeStep: ECustomerImportSteps =
    this.defaultStep || ECustomerImportSteps.WELCOME;
  steps: Record<ECustomerImportSteps, CustomerImportStep> = {
    [ECustomerImportSteps.WELCOME]: {
      componentName: 'Welcome',
      analytics: {
        pageViewLabel: 'Invite Customers/Welcome'
      }
    },
    [ECustomerImportSteps.IMPORT_IN_PROGRESS]: {
      componentName: 'ImportInProgress',
      analytics: {
        pageViewLabel: 'Invite Customers/Import In Progress'
      }
    },
    [ECustomerImportSteps.IMPORT_SUCCESS]: {
      componentName: 'ImportSuccess',
      analytics: {
        pageViewLabel: 'Invite Customers/Import Success'
      }
    },
    [ECustomerImportSteps.OFFER_CREATION]: {
      componentName: 'OfferCreation',
      analytics: {
        pageViewLabel: 'Invite Customers/Campaign Creation'
      }
    },
    [ECustomerImportSteps.SUCCESS]: {
      componentName: 'Success',
      analytics: {
        pageViewLabel: 'Invite Customers/Success'
      }
    },
    [ECustomerImportSteps.ERROR]: {
      componentName: 'Error',
      analytics: {
        pageViewLabel: 'Invite Customers/Error'
      }
    }
  };

  stepProps = {};

  get styleFullScreen() {
    if (!this.mobileFullScreen || !this.$mobileCheck()) return '';
    return `height: ${window.innerHeight}px`;
  }

  get mobileFullScreen() {
    return (
      this.activeStep === ECustomerImportSteps.SUCCESS ||
      this.activeStep === ECustomerImportSteps.OFFER_CREATION
    );
  }

  get activeStepData() {
    return this.steps[this.activeStep];
  }

  get isLastStep() {
    return this.activeStep === Object.keys(this.steps).length;
  }

  mounted() {
    if (!this.disableStatusPolling) {
      eventBus.$emit('stopCheckingCustomerImportStatus');
    }
  }

  beforeDestroy() {
    if (!this.disableStatusPolling) {
      eventBus.$emit('checkCustomerImportStatus');
    }
  }

  @Watch('$route')
  onRouteChange() {
    this.onClose();
  }

  @Watch('activeStep', { immediate: true })
  onChangeStep() {
    const step = this.activeStep;

    const currentStep = this.steps[step];

    if (currentStep.analytics) {
      segmentIo.modal(currentStep.analytics.pageViewLabel);
    }
  }

  onClose() {
    eventBus.$emit('closeModal');
    eventBus.$emit('reloadCustomerImport');
    eventBus.$emit('reloadCustomerImportOverview');
    eventBus.$emit('reloadCustomerImportList');
  }

  handleGoToStep(params: { goToStep: ECustomerImportSteps; stepProps: any }) {
    this.stepProps = params.stepProps;
    this.activeStep = params.goToStep;
  }

  startGuideUpdateTasks() {
    this.onConfirmUpdateStartGuide();
    this.onClose();
    this.$router.push('/home');
  }

  handleEvents({ type, params }: CustomerImportEvent) {
    const eventMapper: Record<string, () => void> = {
      closeModal: () => this.onClose(),
      goToStep: () => this.handleGoToStep(params),
      startGuideUpdateTasks: () => this.startGuideUpdateTasks()
    };

    if (!eventMapper[type]) return;

    eventMapper[type]();
  }
}
